import { useState, useEffect, useCallback } from 'react';
import { Container, Form, ProgressBar } from 'react-bootstrap';
import Stepper from 'react-stepper-horizontal';
import { ApplicantInfo } from '../../components/ApplicantInfo';
import { BurnConditions } from '../../components/BurnPermit/BurnConditions';
import { BurnSite } from '../../components/BurnPermit/BurnSite';
import { BurnType } from '../../components/BurnPermit/BurnType';
import { SubmitSuccess } from '../../components/SubmitSuccess';
import { PageHeader } from '../../components/Layout/PageHeader';
import { DataCollectionNotice } from '../../components/Layout/DataCollectionNotice';
import { InternalServerError } from '../../components/Errors/InternalServerError';
import { SomethingBroke } from '../../components/Errors/SomethingBroke';
import FormClientError from '../../components/Errors/FormClientError';
import { delay } from '../../util/delay';
import dayjs from 'dayjs';

export function BurnPermit() {
	const [step, setStep] = useState(1);

	const [submitDone, setSubmitDone] = useState(false);

	const [httpStatusCode, setHttpStatusCode] = useState(null);

	const [somethingBroke, setSomethingBroke] = useState(false);

	const [currApplicant, setCurrApplicant] = useState({
		name: '',
		phone: '',
		address: '',
		city: '',
		state: 'UT',
		zip: '',
		email: '',
	});
	const [currBurnType, setCurrBurnType] = useState('');

	const [currBurnSite, setCurrBurnSite] = useState({
		acres: '',
		address: '',
		city: '',
		state: 'UT',
		zip: '',
		parcelNumbers: [{ parcelNumber: '' }],
	});

	const [currCondition, setCurrCondition] = useState({ agreed: false });

	const incrementStep = useCallback(() => {
		setStep(curStep => curStep + 1);
	}, []);

	const decrementStep = useCallback(() => {
		setStep(curStep => curStep - 1);
	}, []);

	const submitForm = async condition => {
		try {
			await delay(500); //set a small delay of .5 seconds
			const { status } = await fetch(
				`${process.env.REACT_APP_API_PREFIX_PROTO}${process.env.REACT_APP_API_DOMAIN}${process.env.REACT_APP_API_PREFIX}/burn-permit`,
				{
					headers: { 'Content-Type': 'application/json' },
					method: 'POST',
					body: createBody(condition),
				}
			);
			setHttpStatusCode(status);
			setSubmitDone(false);
		} catch (err) {
			console.error(err);
			setSomethingBroke(true);
		}
	};

	useEffect(() => {
		console.log('httpStatusCode :>> ', httpStatusCode);
		if (httpStatusCode === 200) {
			incrementStep();
		}
	}, [httpStatusCode, incrementStep]);

	const createBody = condition => {
		return JSON.stringify({
			name: currApplicant.name,
			phone: currApplicant.phone,
			city: currApplicant.city,
			email: currApplicant.email,
			state: currApplicant.state,
			mailingAddress: currApplicant.address,
			zip: currApplicant.zip,
			burnSiteAddress: currBurnSite.address,
			burnSiteCity: currBurnSite.city,
			burnSiteState: currBurnSite.state,
			burnSiteZip: currBurnSite.zip,
			date: dayjs().format('MM/DD/YYYY'),
			parcelNumbers: currBurnSite.parcelNumbers.map(
				({ parcelNumber }) => parcelNumber
			),
			acres: currBurnSite.acres,
			burnType: currBurnType,
		});
	};

	const handleOnNext = (setData = () => {}) => {
		return data => {
			setData(data);
			incrementStep();
		};
	};

	const handleSubmit = (setData = () => {}) => {
		return async data => {
			setData(data);
			setSubmitDone(true);
			submitForm(data);
		};
	};

	if (httpStatusCode >= 500) {
		return <InternalServerError></InternalServerError>;
	}

	if (httpStatusCode >= 400) {
		return (
			<FormClientError
				returnToForm={() => {
					setStep(1);
					setHttpStatusCode(null);
				}}
			/>
		);
	}

	if (somethingBroke === true) {
		return <SomethingBroke></SomethingBroke>;
	}

	const steps = [
		{
			component: (
				<ApplicantInfo
					currApplicant={currApplicant}
					onPrevious={decrementStep}
					onNext={handleOnNext(setCurrApplicant)}
					finalStep={false}
					disablePrev={true}
					disableNext={false}
					key="appInfo"
				/>
			),
			title: 'Applicant Information',
		},
		{
			component: (
				<BurnType
					currBurntype={currBurnType}
					onPrevious={decrementStep}
					onNext={handleOnNext(setCurrBurnType)}
					finalStep={false}
					disableNext={false}
					disablePrev={false}
					key="burnType"
				/>
			),
			title: 'Burn Type',
		},
		{
			component: (
				<BurnSite
					currBurnSite={currBurnSite}
					onPrevious={decrementStep}
					onNext={handleOnNext(setCurrBurnSite)}
					finalStep={false}
					disablePrev={false}
					disableNext={false}
					key="burnSite"
				/>
			),
			title: 'Site',
		},
		{
			component: (
				<BurnConditions
					currCondition={currCondition}
					onPrevious={decrementStep}
					onSubmit={handleSubmit(setCurrCondition)}
					finalStep={true}
					disablePrev={false}
					disableNext={false}
					enableLoading={submitDone}
					key="burnConds"
				/>
			),
			title: 'Conditions',
		},
		{
			component: (
				<SubmitSuccess
					finalStep={true}
					disablePrev={true}
					disableNext={true}
					key="submitSuccess"
				/>
			),
			title: 'Submission',
		},
	];

	return (
		<Container fluid className="p-0 mb-5 permit">
			<PageHeader title="Burn Permit" />

			<Form className="app-container ">
				<DataCollectionNotice />

				<div className="stepper">
					<Stepper
						steps={steps.map(({ title }, i) => ({
							title,
							onClick: () => setStep(i + 1),
						}))}
						activeStep={step - 1}
					/>
				</div>

				<div className="bar">
					<ProgressBar className="mb-4" now={(step / steps.length) * 100} />
				</div>

				{steps.map(({ component }, index) =>
					step === index + 1 ? component : ''
				)}
			</Form>
		</Container>
	);
}
